import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import usePopover from '@ui/popover/use-popover';
import { useCallbackRef } from '@/utils/use-callback-ref';
import { AttendeeUserDefault } from '@/types/all-attendees.types';
import type { ImportedProfile } from '@/types/passcode-list.types';
import { getProfileTableHeight, buildSelectAllProfiles, toggleProfileInSelection, saveUsersToLists } from '../../utils';
import { RegFieldsEnum } from '@/types/registration.types';

interface UseProfileSelectionParams {
  users: AttendeeUserDefault[] | null | undefined;
  channel: number | undefined;
  projectUuid: string | undefined;
}

export function useProfileSelection({ users, channel, projectUuid }: UseProfileSelectionParams) {
  const [selectedProfiles, setSelectedProfiles] = useState<ImportedProfile[]>([]);
  const singleUserAddToList = useRef<ImportedProfile[]>([]);
  const addToListPopover = usePopover({ id: 'add-users-to-list' });

  const { displayedUsers, selectedEmails, allDisplayedSelected, someDisplayedSelected } = useMemo(() => {
    const displayedUsers = (users as AttendeeUserDefault[]) ?? [];
    const selectedEmails = new Set(selectedProfiles.map((sp) => sp[RegFieldsEnum.email]));
    const allDisplayedSelected = displayedUsers.length > 0 && displayedUsers.every((u) => selectedEmails.has(u.email));
    const someDisplayedSelected = !allDisplayedSelected && displayedUsers.some((u) => selectedEmails.has(u.email));
    return { displayedUsers, selectedEmails, allDisplayedSelected, someDisplayedSelected };
  }, [selectedProfiles, users]);

  useEffect(() => {
    if (!addToListPopover.open) singleUserAddToList.current = [];
  }, [addToListPopover.open]);

  const handleSelectAll = useCallback(() => {
    setSelectedProfiles(buildSelectAllProfiles(allDisplayedSelected, displayedUsers));
  }, [allDisplayedSelected, displayedUsers]);

  const handleSelectOne = useCallback((profile: ImportedProfile, isOn: boolean) => {
    setSelectedProfiles((prev) => toggleProfileInSelection(prev, profile, isOn));
  }, []);

  const setOpenPopover = useCallbackRef(addToListPopover.setOpenPopover);

  const handleAddSingleUserToList = useCallback(
    (user: AttendeeUserDefault) => {
      singleUserAddToList.current = [
        {
          [RegFieldsEnum.first_name]: user[RegFieldsEnum.first_name] || '',
          [RegFieldsEnum.last_name]: user[RegFieldsEnum.last_name] || '',
          [RegFieldsEnum.email]: user.email,
        },
      ];
      setOpenPopover.current();
    },
    [setOpenPopover]
  );

  const handleSaveAddToList = useCallback(
    (lists: number[]) =>
      saveUsersToLists({
        channel,
        projectUuid,
        lists,
        selectedProfiles,
        singleUserBuffer: singleUserAddToList.current,
        onClearBuffer: () => {
          singleUserAddToList.current = [];
        },
        onClearSelection: () => setSelectedProfiles([]),
      }),
    [channel, projectUuid, selectedProfiles]
  );

  const clearSelection = useCallback(() => setSelectedProfiles([]), []);

  const getHeight = useCallback(() => getProfileTableHeight(selectedProfiles.length > 0), [selectedProfiles.length]);

  return {
    selectedProfiles,
    singleUserAddToList,
    addToListPopover,
    displayedUsers,
    selectedEmails,
    allDisplayedSelected,
    someDisplayedSelected,
    handleSelectAll,
    handleSelectOne,
    handleAddSingleUserToList,
    handleSaveAddToList,
    clearSelection,
    getHeight,
  };
}
